<template>
  <ais-instant-search :search-client="searchClient"
                      :routing="routing"
                      :insights="false"
                      :future="{
                        preserveSharedStateOnUnmount: true
                      }"
                      :index-name="algoliaIndexName">
    <ais-configure :hitsPerPage="40"/>
    <div class="curator-image-editor-viewport">
      <ais-hits>
        <b-row class="h-100" slot-scope="{ items }">
          <b-col lg="2" class="editor-column mb-3">
            <div class="filter-col">
              <div class="filter-header px-2">
                <div class="filter-header-inner d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center py-2">
                    <b-img :src="filterIconURL"></b-img>
                    <span class="font-weight-bold ml-2">FILTERS</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <ais-clear-refinements>
                      <div slot-scope="{ canRefine, refine, createURL }">
                        <a :href="createURL()" @click.prevent="refine" v-if="canRefine"
                           class="btn btn-link d-flex align-items-center p-0">
                          <span class="mr-1">Clear</span>
                          <b-img :src="filterCancelIconURL"></b-img>
                        </a>
                      </div>
                    </ais-clear-refinements>
                  </div>
                </div>
              </div>

              <div class="filter-body p-2">
                <panel name="FEATURED" attribute="rank_featured" :transform-items="transformFeatured"/>
                <panel name="AI APPROVED" attribute="ai_approved" :transform-items="transformFeatured"/>
                <panel name="STATUS" attribute="status" :transform-items="upcaseLabels"/>
                <panel name="USAGE" attribute="usage" :transform-items="upcaseLabels"/>
                <panel name="TYPE" attribute="type" :transform-items="upcaseLabels"/>
                <panel name="TYPES OF PEOPLE" attribute="premium" :transform-items="transformPremium"/>
                <panel name="CATEGORY" attribute="categories" :transform-items="upcaseLabels"/>
                <panel name="RACE" attribute="race" :transform-items="upcaseLabels"/>
                <panel name="ETHNICITY" attribute="ethnicity" :transform-items="upcaseLabels"/>
                <panel name="GENDER / ID" attribute="gender" :transform-items="upcaseLabels"/>
                <panel name="AGES" attribute="ages" :transform-items="upcaseLabels"/>
                <panel name="NUMBER OF PEOPLE" attribute="number_of_people" :transform-items="upcaseLabels"/>
              </div>
            </div>
          </b-col>
          <b-col lg="" class="editor-column">
            <div>
              <div class="filter-search-container d-flex align-items-center px-2">
                <ais-search-box placeholder="Search for assets" class="w-100">
                  <div slot="submit-icon">
                    <b-img :src="filterSearchIconURL"></b-img>
                  </div>
                  <div slot="reset-icon">
                    <b-img :src="filterCancelIconURL"></b-img>
                  </div>
                </ais-search-box>
              </div>

              <div class="py-3">
                <div class="d-flex align-items-center">
                  <div class="">
                    <ais-stats>
                      <p class="font-weight-semi-bold m-0"
                         slot-scope="{ hitsPerPage, nbPages, nbHits, page, processingTimeMS, query }">
                        Displaying <span class="text-primary">{{ nbHits }}</span> assets
                        matching your
                        keywords.
                      </p>
                    </ais-stats>
                  </div>
                  <div class="ml-auto">
                    <ais-pagination
                      :class-names="{'ais-Pagination-list': 'pagination','ais-Pagination-item': 'page-item','ais-Pagination-link': 'page-link', 'ais-Pagination-item--selected': 'active', 'ais-Pagination-item--disabled': 'disabled'}"/>
                  </div>
                </div>
              </div>

              <div class="search-results-container p-3">

                <b-row cols-lg="3" cols-md="2" cols="1" class="align-items-stretch">

                  <b-col v-for="asset in items"
                         class="asset-avatar-select-overlay d-flex flex-column mb-3"
                         :key="asset.objectID">
                    <div
                      class="img-bg text-white asset-square-item-image bottom-actions-hover-trigger asset-avatar-overlay-container cursor-pointer"
                      :style="backgroundImage(asset)" @click="currentSearchAsset = asset">
                      <div @click.prevent.stop="handleAssetSelection(asset)"
                           class="image-select-control d-inline-block">
                        <b-form-checkbox v-model="assetSelection[asset.objectID]"
                                         @click.stop/>
                      </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-1 flex-grow-1">
                      <div class="font-weight-semi-bold text-dark">
                        {{ asset.name }}
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div class="d-flex py-3">
                <div class="ml-auto">
                  <ais-pagination
                    :class-names="{'ais-Pagination-list': 'pagination','ais-Pagination-item': 'page-item','ais-Pagination-link': 'page-link', 'ais-Pagination-item--selected': 'active', 'ais-Pagination-item--disabled': 'disabled'}"/>
                </div>
              </div>

            </div>
          </b-col>
          <b-col cols="auto" class="editor-column">
            <b-modal size="xl" v-model="showBulkModal" hide-footer lazy title="Bulk Asset Management">
              <bulk-actions :selection="selectedAssets(items)"
                            :categories="categories"
                            source="search"
                            @cancel="showBulkModal = false"
              />
            </b-modal>

            <div class="bg-light d-flex flex-column min-h-100 p-2" :class="{'loading': loading}">
              <bulk-selection-bar :items="items"
                                  v-model="assetSelection"
                                  id-key="objectID"
                                  class="py-2"
                                  :bulk-action-running="bulkActionRunning">

                <template v-slot:menuSlot>

                  <b-dropdown variant="outline"
                              v-if="readonly"
                              dropleft
                              no-caret
                              menu-class="dropdown-menu-projects"
                              text="Add to Collection"
                              @shown="$refs.collectionDropdown.fetchCollections()"
                              :disabled="bulkActionRunning">
                    <collection-dropdown :bulk-action-running="bulkActionRunning"
                                         :url="'/curators/collections'"
                                         ref="collectionDropdown"
                                         @addToCollection="handleAddAssets"/>
                  </b-dropdown>
                  <b-button v-else variant="primary" size="sm" @click="showBulkModal = true">
                    Bulk Actions
                  </b-button>
                </template>

              </bulk-selection-bar>

              <asset-details v-model="currentAsset"
                             :readonly="readonly"
                             :categories="categories"
                             :tag-category-order="tagCategoryOrder"
                             :url="assetsUrl"
                             class="flex-grow-1"
                             :curator-request-reasons="curatorRequestReasons"
                             :release-form-url="releaseFormUrl"
                             :submission-release-forms="submissionReleaseForms"
                             @change="handleAssetChange"/>

            </div>
          </b-col>
        </b-row>
      </ais-hits>
    </div>

  </ais-instant-search>
</template>

<script>
import {history as historyRouter} from 'instantsearch.js/es/lib/routers';
import {singleIndex as singleIndexMapping} from 'instantsearch.js/es/lib/stateMappings';
import algoliasearch from 'algoliasearch/lite';
import AssetLoader from "../../../../AssetLoader";
import AssetDetails from "../Submissions/AssetDetails";
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes";
import BulkSelectionBar from "../Submissions/BulkSelectionBar";
import BulkActions from "./Bulk/BulkActions";
import Panel from "./Panel";
import {startCase} from "lodash";
import GeneralMixins from "../../../mixins/GeneralMixins";
import CollectionMixins from "../Collections/CollectionMixins.js";
import CollectionDropdown from "../Collections/CollectionDropdown.vue";

export default {

  props: ['algoliaAppId', 'algoliaSearchKey', 'algoliaIndexName', 'categories', 'tagCategoryOrder', 'tagStore', 'assetsUrl', 'readonly'],

  data() {
    return {
      searchClient: algoliasearch(
        this.algoliaAppId,
        this.algoliaSearchKey
      ),
      routing: {
        router: historyRouter({
          cleanUrlOnDispose: true
        }) ,
        stateMapping: singleIndexMapping(this.algoliaIndexName),
      },

      assetSelection: {},
      currentSearchAsset: null,
      currentAsset: null,
      releaseFormUrl: null,
      submissionReleaseForms: [],
      loading: false,
      bulkActionRunning: false,
      showBulkModal: false
    };
  },

  watch: {
    currentSearchAsset(asset) {
      this.loading = true

      this.$http.get(`/curators/assets/${asset.objectID}/edit.json`).then(fulfilled => {
        this.loading = false
        const body = fulfilled.body

        if (body.submission_release_forms) {
          this.submissionReleaseForms = normalize(JSON.parse(body.submission_release_forms)).get(Attributes.releaseFormAttributes)
        } else {
          this.submissionReleaseForms = []
        }

        this.releaseFormUrl = body.release_form_url

        if (body.asset) {
          this.currentAsset = normalize(JSON.parse(body.asset)).get(Attributes.assetWithIncludesAttributes)
        }
      })
    }
  },

  methods: {
    handleAddAssets(collection) {
      this.updateCollectionAssets(collection)
    },

    upcaseLabels(items) {
      return items.map(item => {
        return {
          ...item,
          label: startCase(item.label)//item.label[0].toUpperCase() + item.label.slice(1)
        };
      });
    },

    transformPremium(items) {
      return items.map(item => {
        return {
          ...item,
          label: item.label.toLowerCase() === 'true' ? 'Celebrities' : 'Everyday People'
        };
      });
    },

    transformFeatured(items) {
      return items.map(item => {
        return {
          ...item,
          label: item.label.toLowerCase() === 'true' ? 'Yes' : 'No'
        };
      });
    },

    backgroundImage(asset) {
      return `background-image: url(${this.assetUpdatedAtImageUrl(asset.thumb_url, asset)})`;
    },

    handleAssetSelection(file) {
      this.$set(this.assetSelection, file.objectID, !this.assetSelection[file.objectID]);
    },

    handleAssetChange(asset) {
      // const existingAsset = this.assets.find(asset => asset.id === asset.id);
      // if (existingAsset) {
      //   this.$set(this.assets, this.assets.indexOf(existingAsset), asset);
      // }
    },

    selectedAssets(pageAssets) {
      const assetIds = Object.keys(this.assetSelection).filter(assetId => this.assetSelection[assetId] === true);
      if (assetIds.length > 0) {
        return assetIds.map(assetId => {
          return pageAssets.find(asset => asset.objectID === assetId)
        })
      }
      return [];
    }
  },

  computed: {
    filterIconURL() {
      return AssetLoader.imagePath('pages/ic-filter.png');
    },

    filterCancelIconURL() {
      return AssetLoader.imagePath('pages/ic-cancel.png');
    },

    filterSearchIconURL() {
      return AssetLoader.imagePath('pages/ic-search.png');
    },

    curatorRequestReasons() {
      return window.reasonStore.curatorRequestReasons
    },

    selectedAssetIds: function () {
      return Object.keys(this.assetSelection).filter(key => this.assetSelection[key] === true);
    },
  },

  components: {AssetDetails, BulkSelectionBar, BulkActions, Panel, CollectionDropdown},
  mixins: [GeneralMixins, CollectionMixins]
};
</script>

<style>

</style>